import React from 'react';
import PropTypes from 'prop-types';
import { Divider, ExpandableText, Skeleton, Text } from '@spark';
import {
    sourceDetailsExampleItems,
    sourceDetailsMetadata,
    sourceDetailsNarrativeRows,
} from '@/features/datasets/utils/datasetSourceConfig';
import '@/features/datasets/components/DataCollection/GenerateDataWithAI/generateWithAI.css';

const NarrativeRow = ({ label, text }) => (
    <div className="gdai-source-details__row">
        <Text as="p" variant="base-semibold" color="primary" className="gdai-source-details__label fs-unmask">
            {label}
        </Text>
        <div className="gdai-source-details__value">
            <ExpandableText text={text} />
        </div>
    </div>
);

NarrativeRow.propTypes = {
    label: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
};

const ExampleBlock = ({ item }) => (
    <div className="gdai-source-details__example">
        <div className="gdai-source-details__example-head">
            <Text
                as="p"
                variant="base-semibold"
                color="primary"
                className="gdai-source-details__label fs-unmask"
            >
                {item.title}
            </Text>
            {item.sideLabel ? (
                <div className="gdai-source-details__example-side">
                    <Text as="span" variant="base-semibold" color="primary" className="fs-unmask">
                        {`${item.sideLabel}:`}
                    </Text>
                    <Text as="span" variant="base-regular" color="secondary" className="fs-unmask">
                        {item.sideValue}
                    </Text>
                </div>
            ) : null}
        </div>
        {item.text ? (
            <Text as="p" variant="base-regular" color="secondary" className="gdai-source-details__example-text fs-unmask">
                {item.text}
            </Text>
        ) : null}
    </div>
);

ExampleBlock.propTypes = {
    item: PropTypes.shape({
        title: PropTypes.string.isRequired,
        text: PropTypes.string,
        sideLabel: PropTypes.string,
        sideValue: PropTypes.string,
    }).isRequired,
};

const MetaRow = ({ row }) => (
    <div className={`gdai-source-details__meta-row${row.wrap ? ' gdai-source-details__meta-row--wrap' : ''}`}>
        <Text
            as="p"
            variant="base-semibold"
            color="primary"
            className="gdai-source-details__meta-label fs-unmask"
        >
            {row.label}
        </Text>
        {row.loading ? (
            <span className="gdai-source-details__meta-loading fs-unmask" data-testid="meta-row-loading">
                <Text as="span" variant="base-regular" color="secondary">
                    In progress
                </Text>
                <Skeleton shapeType="paragraph" width={120} height={10} />
            </span>
        ) : (
            <Text
                as="p"
                variant="base-regular"
                color="secondary"
                className="gdai-source-details__meta-value fs-unmask"
            >
                {row.value}
            </Text>
        )}
    </div>
);

MetaRow.propTypes = {
    row: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        wrap: PropTypes.bool,
        loading: PropTypes.bool,
    }).isRequired,
};

/**
 * Read-only Figma details body (nodes 5132:15799 / 5134:16925).
 * Narrative and examples hide when empty; the metadata grid always renders.
 */
const DatasetSourceDetailsBody = ({
    config,
    raw,
    retrievalInProgress = false,
    configLoading = false,
}) => {
    if (!config) {
        return (
            <Text as="p" variant="base-regular" color="secondary" className="fs-unmask">
                {configLoading
                    ? 'Info is loading...'
                    : 'No saved configuration is available for this dataset.'}
            </Text>
        );
    }

    const narrative = sourceDetailsNarrativeRows(config);
    const examples = sourceDetailsExampleItems(config);
    const metadata = sourceDetailsMetadata(raw, config, { retrievalInProgress });
    const showNarrative = narrative.length > 0;
    const showExamples = examples.length > 0;

    return (
        <div className="gdai-source-details">
            {showNarrative ? (
                <div className="gdai-source-details__section">
                    {narrative.map((row) => (
                        <NarrativeRow key={row.label} label={row.label} text={row.text} />
                    ))}
                </div>
            ) : null}
            {showNarrative && (showExamples || metadata) ? <Divider /> : null}
            {showExamples ? (
                <div className="gdai-source-details__section">
                    {examples.map((item) => (
                        <ExampleBlock key={item.key} item={item} />
                    ))}
                </div>
            ) : null}
            {showExamples ? <Divider /> : null}
            <div className="gdai-source-details__meta">
                <div className="gdai-source-details__meta-col">
                    {metadata.left.map((row) => (
                        <MetaRow key={row.label} row={row} />
                    ))}
                </div>
                <div className="gdai-source-details__meta-col">
                    {metadata.right.map((row) => (
                        <MetaRow key={row.label} row={row} />
                    ))}
                </div>
            </div>
        </div>
    );
};

DatasetSourceDetailsBody.propTypes = {
    config: PropTypes.object,
    raw: PropTypes.object,
    retrievalInProgress: PropTypes.bool,
    configLoading: PropTypes.bool,
};

export default DatasetSourceDetailsBody;
